import React from 'react';
import { Modal, Button, Card, Row, Col, Badge } from 'react-bootstrap';
import { X, InfoCircle } from 'react-bootstrap-icons';
import { FaTags, FaCashRegister, FaTruck, FaBox, FaMortarPestle } from 'react-icons/fa';
import './ItemHelpModal.scss';

interface ItemHelpModalProps {
  show: boolean;
  onHide: () => void;
}

const ItemHelpModal: React.FC<ItemHelpModalProps> = ({ show, onHide }) => {
  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered 
      className="item-help-modal"
    >
      <Modal.Header className="help-modal-header">
        <div className="help-header-content">
          <InfoCircle className="help-icon" />
          <Modal.Title className="help-modal-title">¿Cómo funciona la creación de ítems?</Modal.Title>
        </div>
        <Button variant="link" className="help-close-btn" onClick={onHide}>
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body className="help-modal-body">
        <div className="help-intro">
          <p>Esta guía te ayudará a entender cómo crear productos para tu menú de manera correcta y eficiente.</p>
        </div>

        <div className="help-section">
          <h4><FaTags className="section-icon" />Información Básica</h4>
          
          <div className="help-item">
            <strong>Nombre en carta</strong>
            <p>Es el nombre que verán tus clientes en el menú. Debe ser claro y atractivo.</p>
            <div className="example">
              <strong>Ejemplo:</strong> "Pizza Margarita", "Hamburguesa Clásica"
            </div>
          </div>

          <div className="help-item">
            <strong>Nombre interno</strong>
            <p>Un código corto para identificar el producto internamente.</p>
            <div className="example">
              <strong>Ejemplo:</strong> "pizza_marg", "burguer_clas"
            </div>
          </div>

          <div className="help-item">
            <strong>Precio base</strong>
            <p>El precio principal del producto. <Badge bg="danger">OBLIGATORIO</Badge></p>
            <p>Los precios de delivery y caja se basarán en este si no los cambias.</p>
          </div>

          <div className="help-item">
            <strong>Descripción</strong>
            <p>Describe los ingredientes o características especiales del producto.</p>
          </div>
        </div>

        <div className="help-section">
          <h4><FaTags className="section-icon" />Categoría del Producto</h4>
          
          <div className="help-item">
            <Badge bg="danger">OBLIGATORIO</Badge>
            <p>Debes seleccionar una categoría para organizar tu menú.</p>
            <div className="important-note">
              <InfoCircle className="note-icon" />
              <strong>Sin categoría no puedes crear el ítem.</strong>
            </div>
          </div>
        </div>

        <div className="help-section">
          <h4>Canales de Venta</h4>
          
          <Row>
            <Col md={6}>
              <div className="help-item">
                <strong><FaTruck /> Disponible para delivery</strong>
                <p>El producto se puede vender para entrega a domicilio.</p>
                <p>Puedes poner un precio diferente para delivery (más caro por empaque).</p>
              </div>
            </Col>
            <Col md={6}>
              <div className="help-item">
                <strong><FaCashRegister /> Disponible en caja</strong>
                <p>El producto se puede vender directamente en el local.</p>
                <p>Normalmente es el mismo precio base.</p>
              </div>
            </Col>
          </Row>
        </div>

        <div className="help-section">
          <h4>Control de Stock</h4>
          
          <div className="help-item">
            <p>Puedes activar el control automático de inventario para este producto.</p>
          </div>

          <Row>
            <Col md={6}>
              <Card className="help-card">
                <Card.Body>
                  <h6><FaBox className="scenario-icon" />Control por ítem</h6>
                  <p><strong>¿Cuándo usar?</strong> Para productos que compras ya hechos.</p>
                  <p><strong>Ejemplo:</strong> Bebidas, panes pre-hechos, helados.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="help-card">
                <Card.Body>
                  <h6><FaMortarPestle className="scenario-icon" />Control por ingredientes</h6>
                  <p><strong>¿Cuándo usar?</strong> Para productos que preparas con ingredientes.</p>
                  <p><strong>Ejemplo:</strong> Pizzas, hamburguesas, ensaladas.</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        <div className="help-section">
          <h4>Información Nutricional</h4>
          
          <div className="help-item">
            <p>Marca las características nutricionales y restricciones del producto.</p>
            <p>Esto ayuda a los clientes con alergias o preferencias alimentarias.</p>
            <div className="important-note">
              <InfoCircle className="note-icon" />
              <strong>Tip:</strong> Marcar correctamente esta información evita problemas con clientes y genera confianza.
            </div>
          </div>
        </div>

        <div className="help-section">
          <h4>Imágenes del Producto</h4>
          
          <div className="help-item">
            <p>Agregar imágenes es opcional pero recomendado.</p>
            <p><strong>Beneficio:</strong> Los productos con fotos se venden 3 veces más.</p>
            <div className="example">
              <strong>Consejos:</strong>
              <ul>
                <li>Usa buena iluminación natural</li>
                <li>Muestra el producto completo y atractivo</li>
                <li>Mantén el fondo limpio y simple</li>
              </ul>
            </div>
          </div>
        </div>

      </Modal.Body>

      <Modal.Footer className="help-modal-footer">
        <Button 
          variant="primary" 
          onClick={onHide}
          className="help-got-it-btn"
        >
          ¡Entendido!
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ItemHelpModal;
